import React from 'react'
import { DepositLimitFrequency } from '@mobi/api-types'
import { NoticeBoxSingle } from '@core/Components/NoticeBox'
import { Paragraph } from '@core/Components/Text/Paragraph'
import { PopupNoticeBox } from '@core/Components/PopupNoticeBox'
import { formatDate } from '@mobi/utils'
import { toMoney } from '@mobi/utils/money'
import { CustomerServiceTelephone } from '@mobi/component-library/Common/V2'

export const SuccessMessage = () => (
  <NoticeBoxSingle
    title='Your deposit limit has been set.'
    noticeBoxType='Success'
    hasBorder
    testId='deposit-success'
  />
)

interface CoolingOffNoticeBoxProps {
  coolingOffEndDate: Date
}

export const CoolingOffNoticeBox = ({ coolingOffEndDate }: CoolingOffNoticeBoxProps) => {
  // format the date and replace spaces with non-breaking-spaces ('\u00A0') to prevent line wrapping
  const formattedDate = formatDate(coolingOffEndDate, 'DD MMM YYYY').replace(/\s/g, '\u00A0')

  return (
    <NoticeBoxSingle
      title={
        <span>
          Your increase in deposit limit will be <strong>effective on the {formattedDate}</strong>,
          after the mandatory 7 day cooling off period.
        </span>
      }
      noticeBoxType='Warning'
      hasBorder
      testId='cooling-off-period'
    />
  )
}

interface CurrentFrequencyNoticeBoxProps {
  frequency: DepositLimitFrequency
}

export const CurrentFrequencyNoticeBox = ({ frequency }: CurrentFrequencyNoticeBoxProps) => {
  if (frequency === null) {
    return null
  }

  return (
    <NoticeBoxSingle
      title={
        <span data-tid-current-frequency=''>
          Currently set to <strong>{frequency}</strong>
        </span>
      }
      noticeBoxType='Info'
      hasBorder
      testId='current-frequency'
    />
  )
}

interface CurrentAmountNoticeBoxProps {
  amount: number | null
}

export const CurrentAmountNoticeBox = ({ amount }: CurrentAmountNoticeBoxProps) => {
  if (amount === null) {
    return null
  }

  return (
    <NoticeBoxSingle
      title={
        <span data-tid-current-amount=''>
          Currently set to <strong>{toMoney(amount, { decimalPlaces: 0 })}</strong>
        </span>
      }
      noticeBoxType='Info'
      hasBorder
      testId='current-amount'
    />
  )
}

interface DepositPopupNoticeBoxProps {
  onClose(): void
}

export const ErrorPopupNoticeBox = ({ onClose }: DepositPopupNoticeBoxProps) => {
  const subTitle = (
    <>
      <Paragraph>
        We are experiencing technical difficulties that are stopping you from being able to set your
        deposit limits.
      </Paragraph>
      <Paragraph>
        Please try again or call the Customer Management Centre on{' '}
        <CustomerServiceTelephone isInternational={false} /> or internationally on{' '}
        <CustomerServiceTelephone isInternational={true} />.
      </Paragraph>
    </>
  )

  return (
    <PopupNoticeBox title='Deposit Limits' noticeBoxType='Error' onClose={onClose}>
      {subTitle}
    </PopupNoticeBox>
  )
}

export const SuccessPopupNoticeBox = ({ onClose }: DepositPopupNoticeBoxProps) => {
  return (
    <PopupNoticeBox title='Deposit Limit' noticeBoxType='Success' onClose={onClose}>
      Thank you for reviewing your deposit limit.
    </PopupNoticeBox>
  )
}
